// ============================================================
// DockerEnvironment.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: One Image, Multiple Environments.
// - Key Term: "Twelve-Factor App" (Config in the environment).
// - Strategy: Visualize the .env flow into the container.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import TuneIcon from "@mui/icons-material/Tune";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ConstructionIcon from "@mui/icons-material/Construction";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TERMINAL_BG = "#011627";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(13, 183, 237, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const EnvFile = styled(Paper)(({ theme, type }) => ({
    backgroundColor: TERMINAL_BG,
    padding: theme.spacing(3),
    borderRadius: "12px",
    border: `1px solid ${type === 'prod' ? '#FF5252' : '#ADDB67'}`,
    fontFamily: "'Fira Code', monospace",
    position: 'relative',
    "&::before": {
        content: `".env.${type}"`,
        position: 'absolute',
        top: -10,
        left: 20,
        backgroundColor: type === 'prod' ? '#FF5252' : '#ADDB67',
        color: NAVY_BG,
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.65rem',
        fontWeight: 900
    }
}));

const ConfigCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: "rgba(255,255,255,0.02)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "12px",
    height: "100%",
    transition: "0.3s",
    "&:hover": {
        borderColor: DOCKER_BLUE,
        backgroundColor: "rgba(13, 183, 237, 0.05)"
    }
}));

export default function DockerEnvironment() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Configuration · Module 10"
                            sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Environment <span style={{ color: DOCKER_BLUE }}>Management</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        Hardcoding secrets is a security sin. Docker allows you to inject
                        different settings into the same image depending on where it's running.
                    </Typography>
                </Box>

                {/* 2. THE MULTI-STAGE CONFIG VISUAL */}
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 800, textAlign: 'center' }}>
                    One Image. <span style={{ color: DOCKER_BLUE }}>Infinite Contexts.</span>
                </Typography>



                <Grid container spacing={4} sx={{ mb: 10 }} justifyContent="center">
                    <Grid item xs={12} md={5}>
                        <EnvFile type="dev">
                            <Typography sx={{ color: "#ADDB67", mb: 1 }}>DB_HOST=localhost</Typography>
                            <Typography sx={{ color: "#ADDB67", mb: 1 }}>DEBUG=true</Typography>
                            <Typography sx={{ color: "#ADDB67" }}>API_KEY=dev_secret_123</Typography>
                        </EnvFile>
                    </Grid>

                    <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Stack alignItems="center">
                            <ToggleOnIcon sx={{ fontSize: 60, color: DOCKER_BLUE }} />
                            <Typography variant="overline" sx={{ fontWeight: 900 }}>SWAP</Typography>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <EnvFile type="prod">
                            <Typography sx={{ color: "#FF5252", mb: 1 }}>DB_HOST=db.prodev.cloud</Typography>
                            <Typography sx={{ color: "#FF5252", mb: 1 }}>DEBUG=false</Typography>
                            <Typography sx={{ color: "#FF5252" }}>API_KEY=prod_encrypted_xyz</Typography>
                        </EnvFile>
                    </Grid>
                </Grid>

                {/* 3. CORE TOOLS */}
                <Grid container spacing={3} sx={{ mb: 10 }}>
                    {[
                        {
                            title: "Environment Variables",
                            icon: <VpnKeyIcon />,
                            desc: "Injected at RUNTIME. Used for secrets, DB strings, and feature flags. Best used with .env files."
                        },
                        {
                            title: "Build Arguments (ARG)",
                            icon: <ConstructionIcon />,
                            desc: "Passed during the BUILD phase. Used to set versions or toggle features inside the Dockerfile itself."
                        },
                        {
                            title: "Env Files",
                            icon: <TuneIcon />,
                            desc: "Docker Compose can load entire files of variables using the env_file attribute to keep your YAML clean."
                        },
                        {
                            title: "Standardization",
                            icon: <SettingsSuggestIcon />,
                            desc: "Ensures the application behavior is predictable across Dev, QA, Staging, and Production."
                        }
                    ].map((item, i) => (
                        <Grid item xs={12} sm={6} md={3} key={i}>
                            <ConfigCard>
                                <Box sx={{ color: DOCKER_BLUE, mb: 2 }}>{item.icon}</Box>
                                <Typography variant="subtitle1" fontWeight={800} gutterBottom>{item.title}</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, fontSize: '0.85rem', lineHeight: 1.6 }}>
                                    {item.desc}
                                </Typography>
                            </ConfigCard>
                        </Grid>
                    ))}
                </Grid>

                {/* 4. SECURITY PRO-TIP */}
                <Paper sx={{
                    p: 4,
                    bgcolor: "rgba(255, 82, 82, 0.03)",
                    borderLeft: `5px solid #FF5252`,
                    borderRadius: "0 16px 16px 0"
                }}>
                    <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: "#FF5252" }}>
                        🛡️ Security Alert: .gitignore is your friend
                    </Typography>
                    <Typography variant="body2" sx={{ color: BRAND.muted, mb: 2 }}>
                        Never, ever commit your <code>.env</code> files to Version Control (GitHub/GitLab).
                        Instead, commit an <code>.env.example</code> with empty values to show other developers what variables are needed.
                    </Typography>
                </Paper>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Configured? Let's talk about the Build Lifecycle.</Typography>
                    <Link to="/docker-lifecycle" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(13, 183, 237, 0.3)`,
                                "&:hover": { bgcolor: "#0091ca" }
                            }}
                        >
                            Next: The Docker Lifecycle
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Authorized Content by {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
